import { Action as AccessTokenAction, ActionType as AccessTokenActionType } from '~/AccessToken';
import { Group, List, ProductUpdate, ValidationResult } from '~/Api/User/ProductGroup/CzReimbursement';

import { Action, ActionType } from './Action';

export interface CzReimbursementState {
	request: Promise<any>;
	isFetching: boolean;
	czReimbursement: List | null;
}

interface ScheduledUpdateState {
	name: 'scheduled';
	timeoutId: NodeJS.Timeout;
	packageSize: number | null;
	dosageStrength: number | null;
	usualTherapeuticDailyDose: number | null;
	usualTherapeuticDailyDosePerPackage: number | null;
	products: ProductUpdate[];
}

type UpdateState =
	| ScheduledUpdateState
	| { name: 'fetching'; request: Promise<any> | null }
	| { name: 'updated' }
	| { name: 'failed' };

export interface Update {
	groupId: string;
	state: UpdateState;
}

export type GroupUpdateState =
	| { name: 'fetching'; request: Promise<any> | null }
	| { name: 'updated' }
	| { name: 'failed' };

export interface GroupUpdate {
	groupId: string;
	state: GroupUpdateState;
}

export interface Export {
	groupId: string;
	request: Promise<any>;
	isFetching: boolean;
	validationResult: ValidationResult | null;
}

export interface GroupState {
	id: string;
	group: Group | null;
	isFetching: boolean;
	request: Promise<any>;
}

export interface State {
	byId: { [key in string]: CzReimbursementState };
	update: Update | null;
	export: Export | null;
	groupUpdate: GroupUpdate | null;
	group: GroupState | null;
}

function createState(): State {
	return {
		byId: {},
		update: null,
		export: null,
		groupUpdate: null,
		group: null,
	};
}

export default function reducer(state: State = createState(), action: Action | AccessTokenAction): State {
	switch (action.type) {
		case ActionType.request:
			return {
				...state,
				byId: {
					...state.byId,
					[action.groupId]: {
						request: action.request,
						isFetching: true,
						czReimbursement: state.byId[action.groupId]?.czReimbursement ?? null,
					},
				},
			};

		case ActionType.receive: {
			const czReimbursement = state.byId[action.groupId];
			if (czReimbursement?.request !== action.request) {
				return state;
			}

			if (!action.czReimbursement) {
				const byId = { ...state.byId };
				delete byId[action.groupId];
				return { ...state, byId };
			}

			return {
				...state,
				byId: {
					[action.groupId]: {
						...czReimbursement,
						isFetching: false,
						czReimbursement: action.czReimbursement,
					},
				},
			};
		}

		case ActionType.prune: {
			const byId = { ...state.byId };
			delete byId[action.groupId];
			return {
				...state,
				byId,
				groupUpdate: state.groupUpdate?.groupId === action.groupId ? null : state.groupUpdate,
			};
		}

		case ActionType.scheduleUpdate: {
			const updateState = state.update?.state;
			if (updateState && updateState.name === 'scheduled') {
				clearTimeout(updateState.timeoutId);
			}

			return {
				...state,
				update: {
					groupId: action.groupId,
					state: {
						name: 'scheduled',
						timeoutId: action.timeoutId,
						packageSize: action.packageSize,
						dosageStrength: action.dosageStrength,
						usualTherapeuticDailyDose: action.usualTherapeuticDailyDose,
						usualTherapeuticDailyDosePerPackage: action.usualTherapeuticDailyDosePerPackage,
						products: action.products,
					},
				},
			};
		}

		case ActionType.requestUpdate: {
			return {
				...state,
				update: {
					groupId: action.groupId,
					state: { name: 'fetching', request: action.request },
				},
			};
		}

		case ActionType.receiveUpdate: {
			const updateState = state.update?.state;
			if (updateState && updateState.name === 'fetching' && updateState?.request !== action.request) {
				return state;
			}

			const byId = { ...state.byId };
			const czReimbursementState = byId[action.groupId];

			if (action.czReimbursement && czReimbursementState) {
				byId[action.groupId] = {
					...czReimbursementState,
					czReimbursement: action.czReimbursement,
				};
			}

			return {
				...state,
				byId,
				update: {
					groupId: action.groupId,
					state: action.czReimbursement ? { name: 'updated' } : { name: 'failed' },
				},
				export: null,
			};
		}

		case ActionType.requestXlsCreate:
			return {
				...state,
				export: {
					groupId: action.groupId,
					request: action.request,
					isFetching: true,
					validationResult: null,
				},
			};

		case ActionType.receiveXlsCreate:
			if (state.export?.request !== action.request) {
				return state;
			}

			return {
				...state,
				export: {
					...state.export,
					isFetching: false,
					validationResult: action.validationResult,
				},
			};

		case ActionType.requestGroup:
			return {
				...state,
				group: {
					id: action.groupId,
					request: action.request,
					isFetching: true,
					group: state.group?.id === action.groupId ? state.group.group : null,
				},
			};

		case ActionType.receiveGroup:
			if (state.group?.request !== action.request) {
				return state;
			}

			return {
				...state,
				group: {
					...state.group,
					isFetching: false,
					group: action.group,
				},
			};

		case ActionType.requestGroupUpdate:
			return {
				...state,
				groupUpdate: {
					groupId: action.groupId,
					state: { name: 'fetching', request: action.request },
				},
			};

		case ActionType.receiveGroupUpdate: {
			const updateState = state.groupUpdate?.state;
			if (updateState && updateState.name === 'fetching' && updateState?.request !== action.request) {
				return state;
			}

			return {
				...state,
				group:
					state.group?.id === action.groupId && action.group !== null
						? {
								...state.group,
								group: action.group,
						  }
						: state.group,
				groupUpdate: {
					groupId: action.groupId,
					...(state.groupUpdate || {}),
					state: action.error ? { name: 'failed' } : { name: 'updated' },
				},
			};
		}

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
}

export function selectCzReimbursement(state: State, groupId: string): CzReimbursementState | null {
	return state.byId[groupId];
}

export function selectExport(state: State, groupId: string): Export | null {
	return state.export?.groupId === groupId ? state.export : null;
}

export const selectGroup = (state: State, groupId: string): GroupState | null =>
	state.group !== null && state.group.id === groupId ? state.group : null;
